import { type LocaleDefinition } from '@solinkcorp/locale';
import { getSevenDaysAgoStart } from '@solinkcorp/utils';
import { type HistogramRange } from '@solinkcorp/vd-common';
import { type VideoSearchResult, type VideoSearchStatus } from 'clients/CloudAiApi';
import LocaleUtil from 'utils/LocaleUtil';
import { type VideoSearchType } from './types';

export const RANGE_LOCALE_KEY = {
  Day: 'rangeOnTheDayOf',
  Week: 'rangeWithinTheWeekOf',
  Month: 'rangeWithinTheMonthOf',
  Year: 'rangeWithinTheYearOf',
  Hour: 'rangeWithinTheHourOf',
} as const satisfies Record<HistogramRange, keyof LocaleDefinition['videoSearch']>;

interface SearchRange {
  range: HistogramRange;
  startTs: number;
  endTs: number;
}

/** The range in the search summary, e.g. "Last week (06/16/2026 – 06/23/2026)". */
export function formatSummaryRange(
  { range, startTs, endTs }: SearchRange,
  locale: LocaleDefinition,
): string {
  const format = new Intl.DateTimeFormat(locale.localeCode, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format;
  if (range !== 'Week') return format(startTs);
  return LocaleUtil.getLocalizedStr({
    string: locale.videoSearch.lastWeekRange,
    replacements: { start: format(startTs), end: format(endTs) },
  });
}

/** The range in the date range tip, e.g. "Jul 16 - Jul 23, 2026". */
export function formatTipRange({ range, startTs, endTs }: SearchRange, localeCode: string): string {
  const withYear = new Intl.DateTimeFormat(localeCode, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format;
  if (range !== 'Week') return withYear(startTs);
  const withoutYear = new Intl.DateTimeFormat(localeCode, { month: 'short', day: 'numeric' })
    .format;
  return `${withoutYear(startTs)} - ${withYear(endTs)}`;
}

/**
 * The notice to show for a finished search. `preparing` shows even with results, because other
 * searched customers can still have no searchable video. No notice shows before a search.
 */
export function getSearchNotice({
  status,
  searchType,
  hasResults,
}: {
  status: VideoSearchStatus;
  searchType: VideoSearchType | undefined;
  hasResults: boolean;
}): 'preparing' | 'no-results' | undefined {
  if (status === 'preparing') return 'preparing';
  if (searchType && !hasResults) return 'no-results';
  return;
}

export function getResultStartTime(
  result: Pick<VideoSearchResult, 'timestamp' | 'similarResults'>,
) {
  // If there are similar results and the first result time is before the main result, return the timestamp of the first one
  const firstSimilarResultTime = result.similarResults[0]?.timestamp;
  if (firstSimilarResultTime && firstSimilarResultTime < result.timestamp) {
    return firstSimilarResultTime;
  }

  // Otherwise, return the timestamp of the main result
  return result.timestamp;
}

export function getResultEndTime(result: Pick<VideoSearchResult, 'timestamp' | 'similarResults'>) {
  // If there are similar results and the last result time is after the main result, return the timestamp of the last one
  const lastSimilarResultTime = result.similarResults[result.similarResults.length - 1]?.timestamp;
  if (lastSimilarResultTime && lastSimilarResultTime > result.timestamp) {
    return lastSimilarResultTime;
  }

  // Otherwise, return the timestamp of the main result
  return result.timestamp;
}

export function getResultDuration(result: Pick<VideoSearchResult, 'timestamp' | 'similarResults'>) {
  const startTime = getResultStartTime(result);
  const endTime = getResultEndTime(result);
  return endTime - startTime > 0 ? endTime - startTime : 1000; // Ensure at least 1 second duration
}

export function getVideoSearchRetentionDate(timezoneId: string) {
  return getSevenDaysAgoStart(timezoneId);
}
